// src/components/views/SettingsView.tsx
import React, { useState, useEffect } from 'react';
import { Gamepad2, Bug, Building2, Link, Gem, Trash2 } from 'lucide-react';
import { useSimulationStore } from '../../store/useSimulationStore';
import { authHeaders } from '../../store/useAuthStore';
import { apiUrl } from '../../utils/api';
import Section from '../common/Section';
import { cardInner } from '../../utils/styles';

// ─── Reusable input row ───────────────────────────────────────────────────────
const Field: React.FC<{
  label: string; value: string | number; onChange: (v: string) => void;
  type?: string; placeholder?: string;
}> = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs text-gray-400">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
    />
  </div>
);

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast: React.FC<{ message: string; type: 'success' | 'error'; onClose: () => void }> = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg text-sm font-medium shadow-lg ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {message}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const SettingsView: React.FC = () => {
  const nodes       = useSimulationStore(s => s.nodes);
  const connections  = useSimulationStore(s => s.connections);
  const resourceNodes = useSimulationStore(s => s.resourceNodes);
  const tick         = useSimulationStore(s => s.tick);
  const isConnected  = useSimulationStore(s => s.isConnected);
  const paused       = useSimulationStore(s => s.simulationPaused);
  const setPaused    = useSimulationStore(s => s.setSimulationPaused);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // ── Add node form ──
  const [nodeName, setNodeName]       = useState('');
  const [nodePop, setNodePop]         = useState('1000');
  const [nodeRes, setNodeRes]         = useState('500');
  const [nodeGrowth, setNodeGrowth]   = useState('0.02');
  const [nodeCapacity, setNodeCapacity] = useState('50000');
  const [nodeStatus, setNodeStatus]   = useState<'poor' | 'middle' | 'rich'>('middle');

  // ── Add connection form ──
  const [connA, setConnA] = useState('');
  const [connB, setConnB] = useState('');

  // ── Add resource form ──
  const [resName, setResName]   = useState('');
  const [resX, setResX]         = useState('0');
  const [resY, setResY]         = useState('0');
  const [resRadius, setResRadius] = useState('200');
  const [resYield, setResYield] = useState('400');
  const [resType, setResType]   = useState('generic');

  // ── Delete targets ──
  const [deleteNodeId, setDeleteNodeId] = useState('');
  const [deleteConnId, setDeleteConnId] = useState('');
  const [deleteResId, setDeleteResId]   = useState('');

  // ── Fetch simulation status on mount ──
  useEffect(() => {
    fetch(apiUrl('/api/simulation/status'), { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.paused != null) setPaused(d.paused); })
      .catch(() => {});
  }, [setPaused]);

  const [heartbeatMs, setHeartbeatMs] = useState<number>(5000);
  const [heartbeatInput, setHeartbeatInput] = useState<string>('5000');
  
  useEffect(() => {
    fetch(apiUrl('/api/simulation/heartbeat-interval'), { headers: authHeaders() })
       .then(r => r.json())
       .then(d => { if (d.ok) { setHeartbeatMs(d.interval); setHeartbeatInput(String(d.interval)); } })
       .catch(() => {});
  }, []);
  
  const handleSetHeartbeat = async () => {
    const ms = parseInt(heartbeatInput);
    if (isNaN(ms)) return;
    try {
      const res = await fetch(apiUrl('/api/simulation/heartbeat-interval'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ms }),
      });
      const data = await res.json();
      if (data.ok) { setHeartbeatMs(data.interval); show(`Heartbeat set to ${data.interval}ms`); }
      else show(data.error || 'Failed to set interval', 'error');
    } catch { show('Failed to set interval', 'error'); }
  };
  
  // ── Helpers ─────────────────────────────────────────────────────────────────
  const show = (message: string, type: 'success' | 'error' = 'success') => setToast({ message, type });
  
  const handlePauseResume = async () => {
    try {
      const endpoint = paused ? '/api/simulation/resume' : '/api/simulation/pause';
      const res = await fetch(apiUrl(endpoint), { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.ok) {
        setPaused(data.paused);
        show(data.paused ? 'Simulation paused' : 'Simulation resumed');
      }
    } catch { show('Failed to toggle simulation', 'error'); }
  };

  const handleAddNode = async () => {
    if (!nodeName.trim()) { show('Node name is required', 'error'); return; }
    try {
      const res = await fetch(apiUrl('/api/nodes'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({
        name: nodeName, population: Number(nodePop), resources: Number(nodeRes),
        population_growth_rate: Number(nodeGrowth), capacity_limit: Number(nodeCapacity),
        income_status: nodeStatus, workforce_ratio: 0.5, resource_consumption_rate: 10,
      })});
      if (res.ok) { show(`Node "${nodeName}" created`); setNodeName(''); }
      else show('Failed to create node', 'error');
    } catch { show('Failed to create node', 'error'); }
  };

  const handleDeleteNode = async () => {
    const id = Number(deleteNodeId);
    if (!id) { show('Select a node to delete', 'error'); return; }
    try {
      const res = await fetch(apiUrl(`/api/nodes/${id}`), { method: 'DELETE', headers: authHeaders() });
      if (res.ok) { show(`Node #${id} deleted`); setDeleteNodeId(''); }
      else show('Failed to delete node', 'error');
    } catch { show('Failed to delete node', 'error'); }
  };

  const handleAddConnection = async () => {
    const a = Number(connA), b = Number(connB);
    if (!a || !b || a === b) { show('Select two different nodes', 'error'); return; }
    try {
      const res = await fetch(apiUrl('/api/connections'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ node_a_id: a, node_b_id: b }) });
      if (res.ok) { show(`Connection ${a} ↔ ${b} created`); setConnA(''); setConnB(''); }
      else show('Failed to create connection', 'error');
    } catch { show('Failed to create connection', 'error'); }
  };

  const handleDeleteConnection = async () => {
    const id = Number(deleteConnId);
    if (!id) { show('Select a connection to delete', 'error'); return; }
    try {
      const res = await fetch(apiUrl(`/api/connections/${id}`), { method: 'DELETE', headers: authHeaders() });
      if (res.ok) { show(`Connection #${id} deleted`); setDeleteConnId(''); }
      else show('Failed to delete connection', 'error');
    } catch { show('Failed to delete connection', 'error'); }
  };

  const handleAddResource = async () => {
    if (!resName.trim()) { show('Resource name is required', 'error'); return; }
    try {
      const res = await fetch(apiUrl('/api/resources'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({
        name: resName, x: Number(resX), y: Number(resY), radius: Number(resRadius),
        yield_per_tick: Number(resYield), type: resType,
      })});
      if (res.ok) { show(`Resource "${resName}" created`); setResName(''); }
      else show('Failed to create resource', 'error');
    } catch { show('Failed to create resource', 'error'); }
  };

  const handleDeleteResource = async () => {
    const id = Number(deleteResId);
    if (!id) { show('Select a resource to delete', 'error'); return; }
    try {
      const res = await fetch(apiUrl(`/api/resources/${id}`), { method: 'DELETE', headers: authHeaders() });
      if (res.ok) { show(`Resource #${id} deleted`); setDeleteResId(''); }
      else show('Failed to delete resource', 'error');
    } catch { show('Failed to delete resource', 'error'); }
  };

  const selectClass = "bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500";
  const btnPrimary  = "px-4 py-2 rounded-lg text-sm font-medium transition-colors";
  const btnGreen    = `${btnPrimary} bg-green-600 hover:bg-green-500 text-white`;
  const btnRed      = `${btnPrimary} bg-red-600 hover:bg-red-500 text-white`;

  return (
    <div className="space-y-3">

      {/* ── Simulation Controls ─────────────────────────────────────────────── */}
      <Section title="Simulation Controls" icon={<Gamepad2 className="w-4 h-4" />} variant="settings">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={handlePauseResume} className={paused ? btnGreen : btnRed}>
            {paused ? '▶ Resume Simulation' : '⏸ Pause Simulation'}
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span className={`w-2.5 h-2.5 rounded-full ${paused ? 'bg-yellow-400' : 'bg-green-400 animate-pulse'}`} />
            {paused ? 'Paused' : 'Running'}
          </div>
          <span className="text-xs text-gray-500 font-mono">Tick: {tick}</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap mt-3">
          <label className="text-sm text-gray-400 whitespace-nowrap">Heartbeat interval</label>
          <input
             type="number"
             min={1000}
             max={300000}
             step={500}
             value={heartbeatInput}
             onChange={e => setHeartbeatInput(e.target.value)}
             className="w-28 px-2 py-1 text-sm bg-gray-800 border border-gray-600 rounded text-white"
          />
          <span className="text-sm text-gray-500">ms</span>
          <button onClick={handleSetHeartbeat} className={btnPrimary + ' bg-blue-900/50 hover:bg-blue-900 border border-blue-700 text-blue-300'}>
            Apply
          </button>
          <span className="text-xs text-gray-500">current: {heartbeatMs}ms ({(heartbeatMs / 1000).toFixed(1)}s)</span>
        </div>
      </Section>

      {/* ── Debug Info ──────────────────────────────────────────────────────── */}
      <Section title="Debug Info" icon={<Bug className="w-4 h-4" />} variant="settings">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Nodes',       value: nodes.length,         color: 'text-blue-400' },
            { label: 'Connections',  value: connections.length,   color: 'text-purple-400' },
            { label: 'Resources',    value: resourceNodes.length, color: 'text-emerald-400' },
            { label: 'WS Status',   value: isConnected ? 'Connected' : 'Disconnected', color: isConnected ? 'text-green-400' : 'text-red-400' },
          ].map(s => (
             <div className={`${cardInner} px-3 py-3 text-center`}>
               <div className="text-xs text-gray-500 mb-1">{s.label}</div>
              <div className={`text-lg font-mono font-bold ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-500 font-mono">
          <span>Total pop: {nodes.reduce((s, n) => s + (n.population || 0), 0).toLocaleString()}</span>
          <span>Total resources: {nodes.reduce((s, n) => s + (n.resources || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          <span>Avg stability: {nodes.length ? (nodes.reduce((s, n) => s + (n.stability || 0), 0) / nodes.length * 100).toFixed(1) : 0}%</span>
        </div>
      </Section>

      {/* ── Manage Nodes ────────────────────────────────────────────────────── */}
      <Section title="Manage Nodes" icon={<Building2 className="w-4 h-4" />} variant="settings">
        <div className="space-y-3">
          {/* Add node */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Add Node</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Name"       value={nodeName}     onChange={setNodeName}     placeholder="New City" />
              <Field label="Population"  value={nodePop}      onChange={setNodePop}      type="number" />
              <Field label="Resources"   value={nodeRes}      onChange={setNodeRes}      type="number" />
              <Field label="Growth Rate" value={nodeGrowth}   onChange={setNodeGrowth}   type="number" />
              <Field label="Capacity"    value={nodeCapacity}  onChange={setNodeCapacity} type="number" />
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-400">Income Status</label>
                <select value={nodeStatus} onChange={e => setNodeStatus(e.target.value as any)} className={selectClass}>
                  <option value="poor">Poor</option>
                  <option value="middle">Middle</option>
                  <option value="rich">Rich</option>
                </select>
              </div>
            </div>
            <button onClick={handleAddNode} className={`${btnGreen} mt-3`}>+ Add Node</button>
          </div>

          {/* Delete node */}
          <div className="border-t border-gray-800 pt-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Remove Node</h4>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-400 mb-1 block">Select Node</label>
                <select value={deleteNodeId} onChange={e => setDeleteNodeId(e.target.value)} className={`${selectClass} w-full`}>
                  <option value="">— select —</option>
                  {nodes.map(n => <option key={n.id} value={n.id}>#{n.id} {n.name} (pop {n.population?.toLocaleString()})</option>)}
                </select>
              </div>
              <button onClick={handleDeleteNode} className={`${btnRed} flex items-center gap-1.5`} disabled={!deleteNodeId}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Manage Connections ──────────────────────────────────────────────── */}
      <Section title="Manage Connections" icon={<Link className="w-4 h-4" />} variant="settings">
        <div className="space-y-3">
          {/* Add connection */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Add Connection</h4>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs text-gray-400 mb-1 block">Node A</label>
                <select value={connA} onChange={e => setConnA(e.target.value)} className={`${selectClass} w-full`}>
                  <option value="">— select —</option>
                  {nodes.map(n => <option key={n.id} value={n.id}>#{n.id} {n.name}</option>)}
                </select>
              </div>
              <span className="text-gray-500 pb-2">↔</span>
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs text-gray-400 mb-1 block">Node B</label>
                <select value={connB} onChange={e => setConnB(e.target.value)} className={`${selectClass} w-full`}>
                  <option value="">— select —</option>
                  {nodes.filter(n => String(n.id) !== connA).map(n => <option key={n.id} value={n.id}>#{n.id} {n.name}</option>)}
                </select>
              </div>
              <button onClick={handleAddConnection} className={btnGreen} disabled={!connA || !connB}>+ Connect</button>
            </div>
          </div>

          {/* Delete connection */}
          <div className="border-t border-gray-800 pt-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Remove Connection</h4>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-400 mb-1 block">Select Connection</label>
                <select value={deleteConnId} onChange={e => setDeleteConnId(e.target.value)} className={`${selectClass} w-full`}>
                  <option value="">— select —</option>
                  {connections.map(c => {
                    const a = nodes.find(n => n.id === c.node_a_id);
                    const b = nodes.find(n => n.id === c.node_b_id);
                    return <option key={c.id} value={c.id}>#{c.id} {a?.name || c.node_a_id} ↔ {b?.name || c.node_b_id}</option>;
                  })}
                </select>
              </div>
              <button onClick={handleDeleteConnection} className={`${btnRed} flex items-center gap-1.5`} disabled={!deleteConnId}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Manage Resources ───────────────────────────────────────────────── */}
      <Section title="Manage Resource Nodes" icon={<Gem className="w-4 h-4" />} variant="settings">
        <div className="space-y-3">
          {/* Add resource */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Add Resource Node</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Name"           value={resName}   onChange={setResName}   placeholder="Gold Mine" />
              <Field label="X"              value={resX}      onChange={setResX}      type="number" />
              <Field label="Y"              value={resY}      onChange={setResY}      type="number" />
              <Field label="Radius"         value={resRadius} onChange={setResRadius} type="number" />
              <Field label="Yield / Tick"   value={resYield}  onChange={setResYield}  type="number" />
              <Field label="Type"           value={resType}   onChange={setResType}   placeholder="generic" />
            </div>
            <button onClick={handleAddResource} className={`${btnGreen} mt-3`}>+ Add Resource</button>
          </div>

          {/* Delete resource */}
          <div className="border-t border-gray-800 pt-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Remove Resource Node</h4>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-400 mb-1 block">Select Resource</label>
                <select value={deleteResId} onChange={e => setDeleteResId(e.target.value)} className={`${selectClass} w-full`}>
                  <option value="">— select —</option>
                  {resourceNodes.map(r => <option key={r.id} value={r.id}>#{r.id} {r.name} ({r.type})</option>)}
                </select>
              </div>
              <button onClick={handleDeleteResource} className={`${btnRed} flex items-center gap-1.5`} disabled={!deleteResId}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>
        </div>
      </Section>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default SettingsView;
